import type {
  Change,
  LicenseStatus,
  CreateLicenseRequest,
  Feature,
  License,
  LicenseFeature,
  LicenseProduct,
  Product,
  UpdateLicenseRequest,
} from '@central/api';
import { formatDate } from '@central/ui';
import { fromDateInput, toDateInput } from './format';

/** Editable copy of a license. Numbers stay strings until validated. */
export interface Draft {
  startsAt: string;
  expiresAt: string;
  graceDays: string;
  allowedActivations: string;
  products: LicenseProduct[];
}

export type DraftErrors = Partial<
  Record<'startsAt' | 'expiresAt' | 'graceDays' | 'allowedActivations' | 'products', string>
>;

const off = (featureId: string): LicenseFeature => ({ featureId, enabled: false, quantity: 0 });

/**
 * The editor's starting point: every feature of each licensed product, in
 * catalogue order, with the license's own entries filled in. A feature the
 * license does not list is off with quantity 0.
 */
export function draftFrom(l: License, products: Product[]): Draft {
  return {
    startsAt: toDateInput(l.startsAt),
    expiresAt: toDateInput(l.expiresAt),
    graceDays: String(l.graceDays),
    allowedActivations: String(l.allowedActivations),
    products: l.products.map((lp) => {
      const have = new Map(lp.features.map((f) => [f.featureId, f]));
      const order = products.find((p) => p.id === lp.productId)?.featureIds ?? [];
      const ids = [
        ...order,
        ...lp.features.map((f) => f.featureId).filter((id) => !order.includes(id)),
      ];
      return {
        productId: lp.productId,
        features: ids.map((id) => ({ ...(have.get(id) ?? off(id)) })),
      };
    }),
  };
}

const isInt = (s: string) => /^-?\d+$/.test(s.trim());

export function validate(d: Draft): DraftErrors {
  const e: DraftErrors = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.startsAt)) e.startsAt = 'Enter a start date.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.expiresAt)) e.expiresAt = 'Enter an expiry date.';
  else if (!e.startsAt && d.expiresAt <= d.startsAt)
    e.expiresAt = 'Expiry must be after the start.';
  if (!isInt(d.graceDays) || Number(d.graceDays) < 0) e.graceDays = 'Whole days, 0 or more.';
  if (!isInt(d.allowedActivations) || Number(d.allowedActivations) < -1)
    e.allowedActivations = '−1 for unlimited, 0 for offline only, or a positive number.';
  if (
    d.products.some((p) => p.features.some((f) => !Number.isInteger(f.quantity) || f.quantity < 0))
  )
    e.products = 'Seat quantities are whole numbers, 0 or more.';
  return e;
}

function sameFeature(
  a: LicenseFeature | undefined,
  b: LicenseFeature | undefined,
  featureId: string,
) {
  const x = a ?? off(featureId);
  const y = b ?? off(featureId);
  return x.enabled === y.enabled && x.quantity === y.quantity;
}

function productsChanged(orig: LicenseProduct[], draft: LicenseProduct[]): boolean {
  if (orig.length !== draft.length) return true;
  return draft.some((dp, i) => {
    const op = orig[i]!;
    if (op.productId !== dp.productId) return true;
    const ids = new Set([...op.features, ...dp.features].map((f) => f.featureId));
    return [...ids].some(
      (id) =>
        !sameFeature(
          op.features.find((f) => f.featureId === id),
          dp.features.find((f) => f.featureId === id),
          id,
        ),
    );
  });
}

/**
 * The PATCH body: rev, reason, and only the fields that differ from the
 * loaded license. Features the license did not list and that are still off
 * with no quantity are left out of products.
 */
export function buildUpdate(l: License, d: Draft, reason: string): UpdateLicenseRequest {
  const req: UpdateLicenseRequest = { rev: l.rev, reason: reason.trim() };
  if (d.startsAt !== toDateInput(l.startsAt)) req.startsAt = fromDateInput(d.startsAt);
  if (d.expiresAt !== toDateInput(l.expiresAt)) req.expiresAt = fromDateInput(d.expiresAt);
  if (Number(d.graceDays) !== l.graceDays) req.graceDays = Number(d.graceDays);
  if (Number(d.allowedActivations) !== l.allowedActivations)
    req.allowedActivations = Number(d.allowedActivations);
  if (productsChanged(l.products, d.products)) {
    req.products = d.products.map((dp, i) => {
      const listed = new Set(l.products[i]?.features.map((f) => f.featureId));
      return {
        productId: dp.productId,
        features: dp.features.filter(
          (f) => listed.has(f.featureId) || f.enabled || f.quantity !== 0,
        ),
      };
    });
  }
  return req;
}

/** A new license's starting draft: one year from today (UTC), 14 days' grace, one activation. */
export function newDraft(now: Date = new Date()): Draft {
  const start = now.toISOString().slice(0, 10);
  const end = new Date(Date.UTC(now.getUTCFullYear() + 1, now.getUTCMonth(), now.getUTCDate()));
  return {
    startsAt: start,
    expiresAt: end.toISOString().slice(0, 10),
    graceDays: '14',
    allowedActivations: '1',
    products: [],
  };
}

/** The POST /licenses body for a validated draft. */
export function buildCreate(customerId: string, d: Draft): CreateLicenseRequest {
  return {
    customerId,
    startsAt: fromDateInput(d.startsAt),
    expiresAt: fromDateInput(d.expiresAt),
    graceDays: Number(d.graceDays),
    allowedActivations: Number(d.allowedActivations),
    products: d.products,
  };
}

/** True when the update would change something besides rev and reason. */
export function hasChanges(req: UpdateLicenseRequest): boolean {
  return Object.keys(req).some((k) => k !== 'rev' && k !== 'reason');
}

/** Allowed activations as words: -1 is unlimited, 0 is offline only. */
export function activationsText(n: number): string {
  return n < 0 ? 'unlimited' : n === 0 ? 'offline only' : String(n);
}

/** Human lines for the pending change, e.g. "Signals 400 → 500". */
export function describeUpdate(
  l: License,
  req: UpdateLicenseRequest,
  features: Feature[],
): string[] {
  const out: string[] = [];
  const name = (id: string) => features.find((f) => f.id === id)?.name ?? id;
  if (req.startsAt) out.push(`Starts ${formatDate(l.startsAt)} → ${formatDate(req.startsAt)}`);
  if (req.expiresAt) out.push(`Expires ${formatDate(l.expiresAt)} → ${formatDate(req.expiresAt)}`);
  if (req.graceDays !== undefined) out.push(`Grace ${l.graceDays} → ${req.graceDays} days`);
  if (req.allowedActivations !== undefined)
    out.push(
      `Allowed activations ${activationsText(l.allowedActivations)} → ${activationsText(req.allowedActivations)}`,
    );
  req.products?.forEach((dp, i) => {
    const op = l.products[i];
    for (const f of dp.features) {
      const o = op?.features.find((x) => x.featureId === f.featureId) ?? off(f.featureId);
      const isQty = features.find((x) => x.id === f.featureId)?.isQuantity ?? false;
      if (o.enabled !== f.enabled) out.push(`${name(f.featureId)} ${f.enabled ? 'on' : 'off'}`);
      if (isQty && o.quantity !== f.quantity)
        out.push(`${name(f.featureId)} ${o.quantity} → ${f.quantity}`);
    }
  });
  return out;
}

/** One history line for a stored diff entry; feature ids become names. */
export function describeChange(c: Change, features: Feature[]): string {
  const fmt = (v: unknown) =>
    v === undefined
      ? '–'
      : typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)
        ? formatDate(v)
        : String(v);
  const m = /features\[([^\]]+)\]\.(\w+)$/.exec(c.path);
  if (m) {
    const n = features.find((f) => f.id === m[1])?.name ?? m[1];
    if (m[2] === 'enabled') return `${n} ${c.to ? 'on' : 'off'}`;
    return `${n} ${fmt(c.from)} → ${fmt(c.to)}`;
  }
  if (c.path === 'suspended') return c.to ? 'Suspended' : 'Suspension lifted';
  const labels: Record<string, string> = {
    startsAt: 'Starts',
    expiresAt: 'Expires',
    graceDays: 'Grace days',
    allowedActivations: 'Allowed activations',
  };
  return `${labels[c.path] ?? c.path} ${fmt(c.from)} → ${fmt(c.to)}`;
}

/** Machine ids from a textarea: one per line, trimmed, blank lines dropped. */
export function machineIdLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/** The licenses list's URL search. */
export interface LicensesSearch {
  q?: string;
  customerId?: string;
  status?: LicenseStatus;
  /** Expiring within this many days. */
  expiring?: number;
  new?: boolean;
}

export const licenseStatuses: readonly LicenseStatus[] = [
  'active',
  'expiring',
  'expired',
  'suspended',
];
export const expiringChoices: readonly number[] = [30, 60, 90];
